import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import { ArrowLeft, BarChart3, LineChart, Map, Activity, Grid3X3, Wind, CloudRain, Thermometer } from 'lucide-react'
import { API_BASE } from '../App'

function Visualizations() {
  const [papers, setPapers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${API_BASE}/api/papers?limit=50`)
      .then(r => r.json())
      .then(data => {
        setPapers(data.data || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // 示例图表配置 - 当文献没有可视化时展示
  const demoCharts = [
    {
      title: '全球平均气温异常 (1880-2024)',
      type: 'line',
      icon: Thermometer,
      config: {
        title: { text: '全球平均气温异常 (°C)', left: 'center', textStyle: { fontSize: 16 } },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: ['1880', '1900', '1920', '1940', '1960', '1980', '2000', '2020', '2024'], name: '年份' },
        yAxis: { type: 'value', name: '温度异常 (°C)' },
        series: [{
          data: [-0.16, -0.08, -0.27, 0.12, -0.03, 0.26, 0.54, 1.02, 1.28],
          type: 'line',
          smooth: true,
          lineStyle: { color: '#dc2626', width: 3 },
          itemStyle: { color: '#dc2626' },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(220,38,38,0.3)' }, { offset: 1, color: 'rgba(220,38,38,0.05)' }] } }
        }]
      }
    },
    {
      title: 'ENSO 指数变化 (Niño 3.4)',
      type: 'bar',
      icon: CloudRain,
      config: {
        title: { text: 'ENSO 指数 (Niño 3.4)', left: 'center', textStyle: { fontSize: 16 } },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: ['2020-01', '2020-07', '2021-01', '2021-07', '2022-01', '2022-07', '2023-01', '2023-07', '2024-01'], name: '时间' },
        yAxis: { type: 'value', name: 'SST 异常 (°C)' },
        series: [{
          data: [0.5, -0.8, -0.5, -0.3, -1.0, -1.2, -0.5, 1.5, 2.0],
          type: 'bar',
          itemStyle: {
            color: (params) => params.value > 0 ? '#ef4444' : '#3b82f6'
          }
        }]
      }
    },
    {
      title: '台风生成频率统计',
      type: 'radar',
      icon: Wind,
      config: {
        title: { text: '西北太平洋台风生成频率', left: 'center', textStyle: { fontSize: 16 } },
        radar: {
          indicator: [
            { name: '1月', max: 5 }, { name: '3月', max: 5 }, { name: '5月', max: 10 },
            { name: '7月', max: 15 }, { name: '9月', max: 20 }, { name: '11月', max: 10 }
          ]
        },
        series: [{
          type: 'radar',
          data: [
            { value: [1, 2, 5, 12, 18, 8], name: '2023年', itemStyle: { color: '#3b82f6' }, areaStyle: { opacity: 0.3 } },
            { value: [0, 1, 3, 10, 15, 6], name: '2024年', itemStyle: { color: '#ef4444' }, areaStyle: { opacity: 0.3 } }
          ]
        }]
      }
    }
  ]

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-primary-700">
            <ArrowLeft className="w-5 h-5" />
            返回首页
          </Link>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary-600" />
            可视化中心
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Demo Charts */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">示例可视化</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {demoCharts.map((chart, idx) => {
              const Icon = chart.icon
              return (
                <div key={idx} className="card">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-primary-600" />
                    <h3 className="font-bold text-gray-900">{chart.title}</h3>
                  </div>
                  <ReactECharts option={chart.config} style={{ height: 300 }} />
                </div>
              )
            })}
          </div>
        </div>

        {/* Literature Visualizations */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">文献关联可视化</h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : papers.length === 0 ? (
            <div className="card text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">暂无文献可视化数据</p>
            </div>
          ) : (
            <div className="space-y-8">
              {papers.map(paper => (
                <div key={paper.id} className="card">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded">
                      {paper.category}
                    </span>
                    <Link to={`/paper/${paper.id}`} className="font-bold text-gray-900 hover:text-primary-700">
                      {paper.title_cn || paper.title}
                    </Link>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{paper.abstract_cn || paper.abstract}</p>
                  <div className="flex gap-3">
                    <Link to={`/paper/${paper.id}`} className="btn-primary text-sm">
                      查看详情与图表
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Visualizations
